import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Input, Tooltip, Tag, Row, Col } from 'antd';
import { EyeOutlined, PlusOutlined, SearchOutlined, DeleteOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import dayjs from 'dayjs';
import AddEvent from '../Events/AddEvent';

const EventListAdmin = () => {
  const { darkTheme } = useTheme(); 
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsSearchTerm, setEventsSearchTerm] = useState('');
  const [modalAddVisible, setModalAddVisible] = useState(false);
  const navigate = useNavigate(); 

  const fetchSavedEvents = async () => {
    setLoading(true);
    try {
      const savedEventsResponse = await axios.get('/events/admin');
      setEvents(savedEventsResponse.data);
      setFilteredEvents(savedEventsResponse.data);
    } catch (error) {
      message.error('Error fetching saved events');
      console.error('Error fetching saved events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedEvents();
  }, []);

  const handleView = (event) => {
    navigate('/event-details', {
      state: { 
        event, 
        eventsList: filteredEvents 
      }
    });
  };

  const handleSearchEvents = (e) => {
    const value = e.target.value;
    setEventsSearchTerm(value);

    const filtered = events.filter(event =>
      event.name.toLowerCase().includes(value.toLowerCase()) ||
      event.eventId.toString().includes(value) ||
      event.mnemonic.toLowerCase().includes(value.toLowerCase()) ||
      event.city.toLowerCase().includes(value.toLowerCase()) ||
      event.beginDate.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredEvents(filtered);
  };

  const handleStates = async (record, field) => {
    setLoading(true);
    try {
      const newState = !record[field];
  
      await axios.put(`/events/${record._id}`, {
        [field]: newState,
      });
  
      // Update local status after request
      setFilteredEvents((prevEvents) =>
        prevEvents.map((event) =>
          event._id === record._id
            ? { ...event, [field]: newState }
            : event
        )
      );
  
      const action = field === 'isDeleted' ? (newState ? 'deleted' : 'restored') : (newState ? 'unlocked' : 'locked');
      message.success(`Event "${record.name}" has been ${action} successfully.`);
    } catch (error) {
      message.error(`Error toggling ${field} state. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const clientFilters = Array.from(
      new Set(events.map(ev => ev.clientID?.name).filter(Boolean))
  ).map(name => ({
    text: name,
    value: name
  }));

  const columns = [
    {
      title: 'Client',
      dataIndex: 'clientID',
      key: 'clientID',
      responsive: ['md', 'lg'],
      filters: clientFilters,
      onFilter: (value, record) => record.clientID?.name === value,
      render: (text, record) =>
          record.clientID?.name ? (
              <Tag color={record.clientID?.clientColor || 'default'}>
                {record.clientID.name}
              </Tag>
          ) : (
              <Tag color="red">N/A</Tag>
          ),
      sorter: (a, b) => {
        const nameA = a.clientID?.name?.toLowerCase() || '';
        const nameB = b.clientID?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'ID',
      dataIndex: 'eventId',
      key: 'eventId',
      responsive: ['md', 'lg'],
      sorter: (a, b) => a.eventId - b.eventId,
    },
    {
      title: 'Mnemonic',
      dataIndex: 'mnemonic',
      key: 'mnemonic',
      sorter: (a, b) => {
        const nameA = a.mnemonic?.toLowerCase() || '';
        const nameB = b.mnemonic?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
      render: (text, record) => (
          record.mnemonic ? (
              <Tag color={record.clientID?.clientColor || 'default'}>
                {record.mnemonic}
              </Tag>
          ) : (
              <Tag color="red">N/A</Tag>
          )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      responsive: ['md', 'lg'],
      width: 200, // Adjust the maximum column width
      ellipsis: true, // Enable text truncation if it is too long
      render: (text, record) => {
        const name = record.name[0]?.value || text; // Get the name
        const truncatedName = name.length > 40 ? `${name.substring(0, 40)}...` : name; // Truncate if longer than 50 characters

        return (
            <Tooltip title={name}>
        <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {truncatedName}
        </span>
            </Tooltip>
        );
      },
      sorter: (a, b) => {
        const nameA = (a.name?.[0]?.value || a.name || '').toLowerCase();
        const nameB = (b.name?.[0]?.value || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      responsive: ['md', 'lg'],
    },
    {
      title: 'Begin Date',
      dataIndex: 'beginDate',
      key: 'beginDate',
      responsive: ['md', 'lg'],
      sorter: (a, b) => new Date(a.beginDate) - new Date(b.beginDate),
      render: (text) => {
        return dayjs(text).format('DD-MM-YYYY');
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Tooltip title={record.isActive ? "Locked" : "Unlocked"} color={record.isActive ? "orange" : "red"}>
            <Button 
              icon={record.isActive ? <UnlockOutlined /> : <LockOutlined style={{ color: 'red' }} />} 
              onClick={() => handleStates(record, 'isActive')}
            />
          </Tooltip>
          <Button 
            icon={record.isDeleted ? <DeleteOutlined style={{ color: 'red' }} /> : <DeleteOutlined />} 
            onClick={() => handleStates(record, 'isDeleted')}
          />
        </Space>
      ),
    },
  ];

  const disabledRowStyle = {
    backgroundColor: darkTheme ? '#2a2a2a' : '#f5f5f5', // Dark/light background according to the theme
    color: darkTheme ? '#a1a1a1' : '#b3b3b3', // Lighter or darker text for contrast
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Event List</h1>
      <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
        <Col xs={20} sm={12} md={10} lg={8}>
          <Input
            placeholder="Search by ID, name, mnemonic, city, or begin date"
            prefix={<SearchOutlined />}
            value={eventsSearchTerm}
            onChange={handleSearchEvents}
          />
        </Col>
        <Col xs={4} sm={24} md={8} lg={4}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalAddVisible(true)}
          >
            <span className="hide-text-mobile">Add Event</span>
          </Button>
        </Col>
      </Row>
      
      <Table
        columns={columns}
        dataSource={filteredEvents}
        rowKey="_id"
        loading={loading}
        onRow={(record) => ({
            style: record.isActive ? {} : disabledRowStyle,
        })}
        scroll={{ x: 'max-content' }}
      />
      <AddEvent
        visible={modalAddVisible}
        onClose={() => setModalAddVisible(false)}
        fetchSavedEvents={fetchSavedEvents}
      />
    </div>
  );
};

export default EventListAdmin;
